import type { ReactNode } from 'react';

type RichNode = {
  type?: string;
  text?: string;
  children?: RichNode[];
  tag?: string;
};

const textNode = (text: string, key: string) => (
  <span key={key} className="whitespace-pre-wrap">
    {text}
  </span>
);

const renderRichNodes = (
  nodes: RichNode[] = [],
  parentKey = 'r'
): ReactNode[] =>
  nodes.map((node, idx) => {
    const key = `${parentKey}-${idx}`;
    const type = node.type || 'text';

    if (type === 'text') return textNode(node.text || '', key);

    const children = renderRichNodes(node.children || [], key);

    if (type === 'paragraph') {
      return (
        <p key={key} className="mb-3 last:mb-0 leading-7">
          {children.length ? children : textNode('', `${key}-empty`)}
        </p>
      );
    }

    if (type === 'linebreak') return <br key={key} />;

    if (type === 'list') {
      const isOrdered = node.tag === 'ol';
      const ListTag = isOrdered ? 'ol' : 'ul';
      return (
        <ListTag
          key={key}
          className={isOrdered ? 'list-decimal pl-5 mb-3' : 'list-disc pl-5 mb-3'}
        >
          {children}
        </ListTag>
      );
    }

    if (type === 'listitem') return <li key={key}>{children}</li>;

    if (type === 'heading') {
      return (
        <h3 key={key} className="mb-2 text-lg font-semibold">
          {children}
        </h3>
      );
    }

    return <div key={key}>{children}</div>;
  });

export const parseRichText = (value?: string | null): ReactNode => {
  if (!value?.trim()) {
    return <p className="text-muted-foreground">No content available.</p>;
  }

  try {
    const parsed = JSON.parse(value) as { root?: { children?: RichNode[] } };
    if (parsed?.root?.children) {
      return <div>{renderRichNodes(parsed.root.children)}</div>;
    }
  } catch {
    // fallback to plain text below
  }

  return <p className="leading-7 whitespace-pre-wrap">{value}</p>;
};
